import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { ArrowLeft } from 'lucide-react';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { formatCurrency, formatDate, formatDateTime, formatShortId } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';

const LedgerTransactionDoc = graphql(/* GraphQL */ `
  query LedgerTransaction($id: UUID!) {
    ledgerTransaction(id: $id) {
      id
      transactionId
      amountReceived
      amountTip
      amountCashTrade
      amountRecognizedTotal
      psp
      paymentMethod
      recognitionDate
      recognitionMinute
      createdAt
      categoryName
      deviceName
      categoryId
      deviceId
      order {
        id
      }
      tab {
        id
        name
      }
      payment {
        id
      }
      refund {
        id
      }
      taxLines {
        taxPercentage
        salesExcl
        tax
        salesIncl
      }
    }
  }
`);

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-right text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

/**
 * Ledger transaction detail with VAT tax lines and linked entity IDs.
 */
export function LedgerDetailPage() {
  const backHref = useBackHref('/ledger');
  const { id = '' } = useParams();
  const { data, previousData, loading, error } = useQuery(LedgerTransactionDoc, {
    variables: { id },
  });
  const tx = (data ?? previousData)?.ledgerTransaction;

  if (loading && !tx) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load transaction" description={error.message} />;
  }
  if (!tx) {
    return (
      <EmptyState
        title="Transaction not found"
        description="This ledger row does not exist or has been deleted."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={backHref} id="admin.ledger.back">
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to ledger
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {tx.transactionId}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Created {formatDateTime(tx.createdAt)} · Recognition {formatDate(tx.recognitionDate)}
            </p>
          </div>
        </div>
      </div>

      <OverviewPanel
        stats={[
          {
            id: 'amountReceived',
            label: 'Amount received',
            value: formatCurrency(tx.amountReceived),
          },
          {
            id: 'tip',
            label: 'Tip',
            value: formatCurrency(tx.amountTip),
          },
          {
            id: 'cashTrade',
            label: 'Cash trade',
            value: formatCurrency(tx.amountCashTrade),
          },
          {
            id: 'recognized',
            label: 'Recognized total',
            value: formatCurrency(tx.amountRecognizedTotal),
          },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Summary" className="lg:col-span-1">
          <dl className="space-y-2 text-sm">
            <Row label="Method">{tx.paymentMethod}</Row>
            <Row label="PSP">{tx.psp ?? '—'}</Row>
            <Row label="Category">
              {tx.categoryId ? (
                <Link
                  to={`/categories/${tx.categoryId}`}
                  className="text-brand hover:underline"
                >
                  {tx.categoryName || `#${formatShortId(tx.categoryId)}`}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Device">
              {tx.deviceId ? (
                <Link to={`/devices/${tx.deviceId}`} className="text-brand hover:underline">
                  {tx.deviceName || `#${formatShortId(tx.deviceId)}`}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Recognition minute">{tx.recognitionMinute}</Row>
            <Row label="Order">
              {tx.order ? (
                <Link to={`/orders/${tx.order.id}`} className="text-brand hover:underline">
                  #{formatShortId(tx.order.id)}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Tab">
              {tx.tab ? (
                <Link to={`/tabs/${tx.tab.id}`} className="text-brand hover:underline">
                  {tx.tab.name || `#${formatShortId(tx.tab.id)}`}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Payment">
              {tx.payment ? (
                <Link
                  to={`/payments?search=${encodeURIComponent(tx.payment.id)}`}
                  className="text-brand hover:underline"
                >
                  #{formatShortId(tx.payment.id)}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Refund">
              {tx.refund ? (
                <Link
                  to={`/refunds?search=${encodeURIComponent(tx.refund.id)}`}
                  className="text-brand hover:underline"
                >
                  #{formatShortId(tx.refund.id)}
                </Link>
              ) : (
                '—'
              )}
            </Row>
          </dl>
        </Card>

        <Card title="Tax lines" className="lg:col-span-2">
          {tx.taxLines.length === 0 ? (
            <EmptyState
              title="No tax lines"
              description="This transaction has no VAT breakdown stored."
            />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2">VAT %</th>
                  <th className="px-2 py-2 text-right">Sales excl.</th>
                  <th className="px-2 py-2 text-right">Tax</th>
                  <th className="px-2 py-2 text-right">Sales incl.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                {tx.taxLines.map((line) => (
                  <tr key={String(line.taxPercentage)}>
                    <td className="px-2 py-2">{line.taxPercentage}%</td>
                    <td className="px-2 py-2 text-right">{formatCurrency(line.salesExcl)}</td>
                    <td className="px-2 py-2 text-right">{formatCurrency(line.tax)}</td>
                    <td className="px-2 py-2 text-right font-medium">
                      {formatCurrency(line.salesIncl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
